import { Button } from '@sitewise/ui';
import { Download, Smartphone } from 'lucide-react';
import { useState } from 'react';

import { useSyncStore } from '../../../stores/sync-store.js';

/**
 * Phase 10 — "Install SiteWise" section on the profile settings page.
 *
 * Chrome / Edge: uses the cached `beforeinstallprompt` event captured
 * in `register-sw.ts`. Calling `prompt()` shows the native install
 * dialog and we clear our cached event once the user picks.
 *
 * iOS / Safari: no programmatic install API exists, so we render a
 * three-step walkthrough instead. The user copies these steps to add
 * SiteWise to their home screen.
 */
export function InstallAppCard() {
  const installPrompt = useSyncStore((s) => s.installPrompt);
  const setInstallPrompt = useSyncStore((s) => s.setInstallPrompt);
  const [showIos, setShowIos] = useState(false);

  const isStandalone =
    typeof window !== 'undefined' &&
    (window.matchMedia?.('(display-mode: standalone)').matches ||
      (window.navigator as Navigator & { standalone?: boolean }).standalone === true);

  if (isStandalone) {
    return (
      <Card title="Install SiteWise">
        <p className="text-sm text-fg-muted">
          SiteWise is installed and running as a standalone app on this device.
        </p>
      </Card>
    );
  }

  async function handleInstall() {
    if (!installPrompt) return;
    await installPrompt.prompt();
    const choice = await installPrompt.userChoice;
    if (choice.outcome === 'accepted') {
      setInstallPrompt(null);
    }
  }

  return (
    <Card title="Install SiteWise">
      <p className="text-sm text-fg-muted">
        Install the app for faster launch, offline access to your inspections, and
        push-style notifications.
      </p>
      <div className="mt-3 flex gap-2">
        {installPrompt ? (
          <Button variant="default" onClick={handleInstall}>
            <Download className="h-4 w-4" />
            Install
          </Button>
        ) : null}
        <Button variant="outline" onClick={() => setShowIos((v) => !v)}>
          <Smartphone className="h-4 w-4" />
          iOS instructions
        </Button>
      </div>
      {showIos ? (
        <ol className="mt-3 list-decimal space-y-1 pl-5 text-sm text-fg-secondary">
          <li>Open SiteWise in Safari on your iPhone or iPad.</li>
          <li>Tap the Share button at the bottom of the screen.</li>
          <li>
            Scroll and tap <strong>Add to Home Screen</strong>, then{' '}
            <strong>Add</strong>.
          </li>
        </ol>
      ) : null}
    </Card>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-border bg-surface-elevated p-4">
      <h2 className="text-sm font-semibold tracking-tight text-fg">{title}</h2>
      <div className="mt-2">{children}</div>
    </div>
  );
}
